import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, Target, Lightbulb, TrendingUp } from "lucide-react";
import api from "../lib/api";
import { Reveal } from "../components/Reveal";
import { usePageMeta } from "../lib/seo";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);

  usePageMeta({
    title: c ? c.title : "Case Study",
    description: c ? c.summary : "AI case study by Growing IT Solutions.",
    path: `/case-studies/${slug}`,
    image: c?.cover_image,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/case-studies/${slug}`).then((r) => setC(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen grid place-items-center px-6 pt-20">
        <div className="text-center">
          <h1 className="font-heading text-3xl font-semibold">Case study not found</h1>
          <Link to="/case-studies" className="inline-flex items-center gap-2 mt-6 text-primary font-semibold">
            <ArrowLeft className="w-4 h-4" /> Back to case studies
          </Link>
        </div>
      </div>
    );
  }

  if (!c) {
    return <div className="min-h-screen grid place-items-center pt-20"><div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="case-detail-page">
      {/* Hero */}
      <section className="relative border-b border-white/10">
        <div className="absolute inset-0">
          <img src={c.cover_image} alt={c.title} className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/85 to-background" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 sm:px-10 pt-36 pb-16 sm:pt-44">
          <Link to="/case-studies" data-testid="back-to-cases" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" /> All case studies
          </Link>
          <Reveal>
            <span className="text-xs bg-primary/15 text-primary border border-primary/30 px-3 py-1">{c.category}</span>
            <h1 className="font-heading text-3xl sm:text-5xl font-semibold mt-5 leading-tight max-w-3xl">{c.title}</h1>
            <p className="mt-5 text-slate-300 text-lg max-w-2xl leading-relaxed">{c.summary}</p>
            <p className="mt-4 text-sm text-slate-500">Client focus: {c.client}</p>
          </Reveal>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-16 sm:py-20">
        {/* Metric highlight */}
        <Reveal>
          <div className="flex flex-col sm:flex-row gap-6 sm:items-center border border-primary/30 bg-[#0F111A] p-8 mb-14 glow-blue">
            <div className="font-heading text-5xl font-semibold text-primary">{c.metric_value}</div>
            <div className="text-slate-300">{c.metric_label}</div>
          </div>
        </Reveal>

        {/* Challenge / Solution */}
        <div className="grid md:grid-cols-2 gap-8 mb-14">
          <Reveal>
            <div className="border border-white/10 bg-[#0F111A] p-8 h-full">
              <Target className="w-8 h-8 text-primary mb-4" />
              <h2 className="font-heading text-xl font-semibold text-white mb-3">The Challenge</h2>
              <p className="text-slate-400 leading-relaxed">{c.challenge}</p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="border border-white/10 bg-[#0F111A] p-8 h-full">
              <Lightbulb className="w-8 h-8 text-primary mb-4" />
              <h2 className="font-heading text-xl font-semibold text-white mb-3">Our Solution</h2>
              <p className="text-slate-400 leading-relaxed">{c.solution}</p>
            </div>
          </Reveal>
        </div>

        {/* Features */}
        {c.features?.length > 0 && (
          <Reveal>
            <div className="mb-14">
              <h2 className="font-heading text-2xl font-semibold mb-6">Key features delivered</h2>
              <ul className="grid sm:grid-cols-2 gap-4">
                {c.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 border border-white/10 bg-[#0F111A] p-5">
                    <span className="w-6 h-6 grid place-items-center bg-primary/15 text-primary shrink-0">
                      <Check className="w-4 h-4" />
                    </span>
                    <span className="text-slate-200 text-sm leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        )}

        {/* Results */}
        {c.results?.length > 0 && (
          <Reveal>
            <div className="mb-14 border border-white/10 bg-gradient-to-br from-[#0F111A] to-[#070810] p-8 sm:p-10">
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp className="w-7 h-7 text-primary" />
                <h2 className="font-heading text-2xl font-semibold">Results &amp; Impact</h2>
              </div>
              <ul className="space-y-4">
                {c.results.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-200">
                    <span className="text-primary mt-1">▹</span>
                    <span className="leading-relaxed">{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        )}

        {/* Tech stack */}
        <Reveal>
          <div className="mb-16">
            <h2 className="font-heading text-2xl font-semibold mb-6">Technology stack</h2>
            <div className="flex flex-wrap gap-3">
              {c.technologies.map((t) => (
                <span key={t} className="text-sm font-mono text-slate-300 border border-white/10 bg-[#0F111A] px-4 py-2">{t}</span>
              ))}
            </div>
          </div>
        </Reveal>

        {/* CTA */}
        <div className="border-t border-white/10 pt-12 text-center">
          <h3 className="font-heading text-2xl sm:text-3xl font-semibold">Want results like these?</h3>
          <Link to="/contact" className="inline-flex items-center gap-2 mt-6 bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-4 glow-blue transition-all">
            Start your project <ArrowUpRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
